import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import breakpointValues, { BreakpointValueProp } from "@store/Framework/Common/Utilities/breakpointValues";
import getCleanBreakpointValues from "@store/Framework/Common/Utilities/getCleanBreakpointValues";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import ChevronDown from "@store/Library/Components/Icons/ChevronDown";
import ChevronLeft from "@store/Library/Components/Icons/ChevronLeft";
import ChevronRight from "@store/Library/Components/Icons/ChevronRight";
import ChevronUp from "@store/Library/Components/Icons/ChevronUp";
import CarouselButton, {
    CarouselButtonPresentationProps,
    CarouselButtonProps,
} from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import React, { FC, HTMLAttributes, useMemo } from "react";
import { css } from "styled-components";

export interface CarouselArrowIcons {
    up: CarouselButtonPresentationProps;
    right: CarouselButtonPresentationProps;
    down: CarouselButtonPresentationProps;
    left: CarouselButtonPresentationProps;
}

export type CarouselArrowDirection = keyof CarouselArrowIcons;
export type CarouselArrowPosition = "top" | "right" | "bottom" | "left";

const getDirectionFromPosition = (position: CarouselArrowPosition | undefined): CarouselArrowDirection | undefined => {
    return position === "top" ? "up" : position === "bottom" ? "down" : position;
};

export interface CarouselArrowPresentationProps extends ExpandStyles<CarouselArrowStyles> {
    direction?: CarouselArrowDirection;
    icons?: CarouselArrowIcons;
    position?: CarouselArrowPosition;
    positionOffset?: BreakpointValueProp;
    positionOrigin?: BreakpointValueProp;
}

export interface CarouselArrowProps extends CarouselArrowPresentationProps, HTMLAttributes<HTMLDivElement> {
    carouselButton: CarouselButtonProps;
}

export interface CarouselArrowStyles {
    carouselButton?: CarouselButtonPresentationProps;
}

export const carouselArrowStyles: CarouselArrowStyles = {
    carouselButton: {
        iconSize: "0.63em",
    },
};

const CarouselArrow: FC<CarouselArrowProps> = ({
    carouselButton,
    children,
    direction: baseDirection,
    extendedStyles,
    overridedStyles,
    position,
    positionOffset: basePositionOffset,
    positionOrigin: basePositionOrigin,
    icons = {
        up: { iconSrc: ChevronUp, iconMargin: "-0.07em 0 0 0" },
        right: { iconSrc: ChevronRight, iconMargin: "0 -0.07em 0 0" },
        down: { iconSrc: ChevronDown, iconMargin: "0 0 -0.07em 0" },
        left: { iconSrc: ChevronLeft, iconMargin: "0 0 0 -0.07em" },
    },
    ...htmlAttributes
}) => {
    const direction = baseDirection || getDirectionFromPosition(position);
    const isVertical = position === "top" || position === "bottom";
    const positionOffset = getCleanBreakpointValues(basePositionOffset);
    const positionOrigin = getCleanBreakpointValues(basePositionOrigin);
    const styles = useExpandProps(carouselArrowStyles, extendedStyles, overridedStyles);

    const carouselButtonStyles = useMemo(() => {
        return mergeProps<CarouselArrowStyles["carouselButton"]>(styles.carouselButton, {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${() => {
                            if (position) {
                                const originSign = position === "top" || position === "left" ? "-" : "";

                                return css`
                                    position: absolute;
                                    ${isVertical ? "left" : "top"}: 50%;
                                    ${position}: 0;

                                    ${breakpointValues(
                                        positionOffset?.map(x => {
                                            if (!x && x !== 0) {
                                                return null;
                                            }

                                            return css`
                                                ${`margin-${position}`}: ${x};
                                            `;
                                        }),
                                    )}

                                    ${breakpointValues(
                                        positionOrigin?.map(x => {
                                            if (!x && x !== 0) {
                                                return null;
                                            }

                                            return css`
                                                transform: translate3d(
                                                    ${isVertical
                                                        ? `-50%, ${originSign}${x}`
                                                        : `${originSign}${x}, -50%`},
                                                    0
                                                );
                                            `;
                                        }),
                                    )}
                                `;
                            }
                        }}
                    `,
                },
            },
        });
    }, [position, positionOffset, positionOrigin, styles.carouselButton]);

    return (
        <CarouselButton
            {...(direction && icons?.[direction])}
            {...carouselButtonStyles}
            {...carouselButton}
            {...htmlAttributes}
            className={`arrow${isVertical ? " vertical" : " horizontal"}${
                carouselButton.className ? ` ${carouselButton.className}` : ""
            }`}
        />
    );
};

export default CarouselArrow;
